import type { FieldAudience } from './types';

/**
 * Field audience registry — which agent fields may reach the live avatar.
 *
 * Why this exists as DATA rather than a comment:
 * `systemPromptBuilder.ts` documented these exclusions in comments
 * (`⚠️ EXCLUDED: objectives field (👁️ REP ONLY ...)`). During the V2 design
 * review an experienced reviewer read that file and still proposed compiling
 * `objectives` and `expected_answers` into the avatar prompt — twice. Comments
 * do not survive a rewrite of three prompt builders. This registry does, and
 * `assertNoLeak()` fails the build if it is ever violated.
 *
 * The two failure modes this prevents are silent, not loud:
 *  - examiner + expected_answers -> the examiner leaks model answers as hints,
 *    destroying the assessment while appearing to work.
 *  - roleplay + objectives -> the agent learns what the learner is supposed to
 *    elicit, and volunteers it. The session looks fine; the training value is gone.
 */
export const FIELD_AUDIENCE: Record<string, FieldAudience> = {
  // ---- learner-facing: preparation material, shown before the session ----
  objectives: 'learner',
  prep_materials: 'learner',
  brief_description: 'learner',

  // ---- evaluator-only: scoring material, used after the session ----
  expected_answers: 'evaluator',
  pass_criteria: 'evaluator',
  evaluation_rubric: 'evaluator',
  evaluation_knowledge_sources: 'evaluator',
  goal_score: 'evaluator',
  show_rubric_to_users: 'evaluator',

  // ---- agent-visible: safe to compile into the live prompt ----
  name: 'agent',
  age: 'agent',
  gender: 'agent',
  occupation: 'agent',
  personality: 'agent',
  backstory: 'agent',
  communication_style: 'agent',
  conversation_tone: 'agent',
  disclosure_style: 'agent',
  coaching_style: 'agent',
  examiner_personality: 'agent',
  scenario_context: 'agent',
  roleplay_focus: 'agent',
  instructions: 'agent',
  first_message: 'agent',
  industry: 'agent',
  company_organization: 'agent',
  prospect_role: 'agent',
  budget_authority: 'agent',
  pain_points: 'agent',
  common_objections: 'agent',
  buying_criteria: 'agent',
  presenting_complaint: 'agent',
  medical_history: 'agent',
  clinical_specialty: 'agent',
  examination_type: 'agent',
  station_title: 'agent',
  case_summary: 'agent',
  key_questions: 'agent',
  followup_probes: 'agent',
  knowledge_sources: 'agent',
  difficulty: 'agent',
  type: 'agent',
};

/** Fields that must NEVER appear in a compiled live prompt. */
export const FORBIDDEN_IN_PROMPT = Object.entries(FIELD_AUDIENCE)
  .filter(([, audience]) => audience !== 'agent')
  .map(([field]) => field);

export function audienceOf(field: string): FieldAudience {
  // Unknown fields default to the safest answer: not the agent's business.
  return FIELD_AUDIENCE[field] ?? 'evaluator';
}

export function isAgentVisible(field: string): boolean {
  return audienceOf(field) === 'agent';
}

/**
 * Guard: assert that no learner/evaluator-only *content* reached the prompt.
 *
 * Compares the compiled prompt against the agent's actual values for forbidden
 * fields, rather than field names, because it is the content that leaks.
 * Short values are ignored to avoid false positives on incidental matches.
 */
export function findLeaks(prompt: string, agent: Record<string, unknown>): string[] {
  const haystack = prompt.toLowerCase();
  const leaks: string[] = [];

  for (const field of FORBIDDEN_IN_PROMPT) {
    const raw = agent[field];
    if (!raw) continue;
    const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
    const needle = text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    // Only meaningful spans can leak; ignore trivial/short values.
    if (needle.length < 40) continue;
    const probe = needle.slice(0, 60).toLowerCase();
    if (haystack.includes(probe)) leaks.push(field);
  }

  return leaks;
}

export function assertNoLeak(prompt: string, agent: Record<string, unknown>): void {
  const leaks = findLeaks(prompt, agent);
  if (leaks.length > 0) {
    throw new Error(
      `Prompt V2 audience violation — these are not the agent's to know: ${leaks.join(', ')}. ` +
        `Learner objectives and evaluator material must never reach the live avatar.`,
    );
  }
}
